import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { GridLegacy as Grid } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { LeadListTypeIcon } from 'lead-list/components/LeadListTypeIcon/LeadListTypeIcon';
import { TooltipBase } from 'lead-list/components/TooltipBase';
import { FC, ReactNode } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import { ListType } from 'types/apollo/globalTypes';
import { UserAccess_userAccess_organizations } from 'types/apollo/UserAccess';
import messages from '../messages';

interface LeadListReviewProps {
  leads: number;
  fileName: string;
  company: UserAccess_userAccess_organizations;
  campaign?: string;
  listType: ListType;
}

export const MappedItemsCategoryText: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <Typography color="textSecondary" sx={{ pl: 1 }}>
    {children}
  </Typography>
);

export const LeadListReview: FC<LeadListReviewProps> = ({
  leads,
  fileName,
  listType,
  company,
  campaign,
}) => {
  const { formatMessage } = useIntl();

  let headingMessage: { id: string; defaultMessage: string };
  switch (listType) {
    case ListType.PROSPECTIVE:
      headingMessage = messages.reviewLeadNumberImportedNew;
      break;
    case ListType.CLOSE:
      headingMessage = messages.reviewLeadNumberImportedClose;
      break;
    case ListType.DNC:
      headingMessage = messages.reviewLeadNumberImportedDnc;
      break;
    case ListType.ENDCONTACT:
      headingMessage = messages.reviewLeadNumberImportedEndContact;
      break;
  }

  const hasCompanyName = !!company?.name && company.name.trim().length > 0;
  const hasCampaign = !!campaign && campaign.trim().length > 0;

  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="h4" sx={{ fontWeight: 400, mb: 3, pr: 3 }}>
        <Typography variant="h4" component="span">
          <FormattedNumber value={leads} />
        </Typography>{' '}
        {formatMessage(headingMessage)}
      </Typography>

      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={4}>
          <MappedItemsCategoryText>
            {formatMessage(messages.reviewFileName)}
          </MappedItemsCategoryText>
        </Grid>
        <Grid item xs={8}>
          <TooltipBase title={fileName}>
            <Typography noWrap>{fileName}</Typography>
          </TooltipBase>
        </Grid>

        <Grid item xs={4}>
          <MappedItemsCategoryText>
            {formatMessage(messages.reviewLeadType)}
          </MappedItemsCategoryText>
        </Grid>
        <Grid item xs={8}>
          <LeadListTypeIcon listType={listType} withLabel />
        </Grid>

        {hasCompanyName && (
          <>
            <Grid item xs={4}>
              <MappedItemsCategoryText>
                {formatMessage(messages.reviewCompany)}
              </MappedItemsCategoryText>
            </Grid>
            <Grid item xs={8}>
              <Typography noWrap>{company.name}</Typography>
            </Grid>
          </>
        )}

        {hasCampaign && (
          <>
            <Grid item xs={4}>
              <MappedItemsCategoryText>
                {formatMessage(messages.reviewCampaign)}
              </MappedItemsCategoryText>
            </Grid>
            <Grid item xs={8}>
              <Typography noWrap>{campaign}</Typography>
            </Grid>
          </>
        )}

        <Grid item xs={4}>
          <MappedItemsCategoryText>
            {formatMessage(messages.reviewColumnsMapped)}
          </MappedItemsCategoryText>
        </Grid>
        <Grid item xs={8}>
          <CheckCircleIcon sx={{ width: 20, height: 20, color: '#00C389' }} />
        </Grid>
      </Grid>

      {listType === ListType.PROSPECTIVE && (
        <Typography variant="body1" style={{ marginTop: '16px', lineHeight: '1.75' }}>
          {formatMessage(messages.reviewClarification)}
        </Typography>
      )}
    </Box>
  );
};
